// ---------------------------------------------------------------------------
// The player bot — plays the way a real player does, from fitted habits
// ---------------------------------------------------------------------------
//
// playerPolicy(me, oppBoard, oppGrid, rng, model) → orders, drawn from the
// rates scripts/fit-player-model.js measures in a player's archived wars
// (playerModel.js): when they attack at each edge, where their deploys go,
// how often they brood, move and buy. Used three ways:
//   - as a test opponent that plays like that player (BOTS.mimic)
//   - as the search bot's model of its opponent (opponentModel 'player')
//   - as the opponent in the search bot's look-ahead
//
// It decides from what that side can see: its own side whole, the other
// side's board, and the other Grid as the fog shows it (oppGrid,
// view.viewOfOpponentGrid). It aims like a player reading the board
// (stampOdds): known structure cells first, then what the intel stamps say —
// nothing live closer to a stamp than its distance, the stamped structure
// somewhere on the ring at exactly that distance — and puts the crater its
// edge will dig where it covers the most of that.

import { rules } from '../config.js'
import {
    moveUnit, deployArmy, buildFort, canAttack, canPlaceDropZone, requiredDropZones, calculateCombatStrength, basesOf,
} from '../gameLogic.js'
import { eligibleBaseCenters, baseFootprint } from '../subplotBases.js'
import { plotSubIndices, plotOf } from '../subplotGrid.js'
import { craterFootprint } from '../subplotDamage.js'
import { hexDistance } from '../hexGrid.js'
import { validateOrders } from '../validateOrders.js'
import { openSlots, deployOnly, assetCells } from './randomBot.js'
import { pick } from './rng.js'
import MODEL from './playerModel.js'

export const PLAYER_MODEL = MODEL
const clone = (v) => JSON.parse(JSON.stringify(v))
const room = (bf) => bf.maxArmyQty - ((bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0))
const corridorCells = (col) => [0, 1, 2, 3, 4].flatMap(row => plotSubIndices(col, row))
const oppStrength = (bf) => calculateCombatStrength(bf) + (bf.newArmies || 0)
const edgeKey = (e) => (e <= -2 ? '-2' : e >= 3 ? '+3' : e > 0 ? `+${e}` : String(e))

function weightedPick(rng, items, weight) {
    const ws = items.map(weight)
    const total = ws.reduce((a, b) => a + b, 0)
    if (total <= 0) return pick(rng, items)
    let r = rng() * total
    for (let i = 0; i < items.length; i++) { r -= ws[i]; if (r <= 0) return items[i] }
    return items[items.length - 1]
}

// Where a player reading the board expects the opponent's structures: per
// cell, 1 for a known live structure cell; 0 for anything known empty,
// destroyed, or closer to any stamp than that stamp's distance; otherwise the
// share of live stamps whose ring (exactly their distance) runs through it,
// plus a little for unknown land. Cheap — no sampling.
export function stampOdds(oppGrid) {
    const n = oppGrid.cells.length
    const odds = new Float64Array(n)
    const stamps = oppGrid.stamps.filter(t => t.distance != null)
    const live = stamps.filter(t => !t.dead)
    const broodCells = new Set([...oppGrid.broods.flatMap(b => b.footprint), ...(oppGrid.pastBroodCells ?? [])])
    for (let s = 0; s < n; s++) {
        const c = oppGrid.cells[s]
        if (c.destroyed || broodCells.has(s)) continue
        if (c.known === 'structure') { odds[s] = 1; continue }
        if (c.known === 'empty') continue
        if (stamps.some(t => hexDistance(s, t.s) < t.distance)) continue
        const onRing = live.filter(t => hexDistance(s, t.s) === t.distance).length
        odds[s] = 0.05 + (live.length ? 0.6 * onRing / live.length : 0)
    }
    return odds
}

// What a player with this edge can reach in a corridor: 'known' when a
// crater could cover a known live structure cell or a brood being built,
// 'stamps' when the stamps put a structure's worth of odds in reach, else
// 'none'. The fitted attack rate depends on it (playerModel.attackByTarget).
export function targetInReach(oppGrid, col, margin, odds = stampOdds(oppGrid)) {
    const conBroods = new Set(oppGrid.broods.filter(b => !b.spent && b.underConstruction).flatMap(b => b.footprint))
    let best = 0
    for (const s of corridorCells(col)) {
        const fp = craterFootprint(s, margin)
        if (fp.some(x => odds[x] === 1 || conBroods.has(x))) return 'known'
        best = Math.max(best, fp.reduce((a, x) => a + odds[x], 0))
    }
    return best >= 1 ? 'stamps' : 'none'
}

// P(attack) from a corridor holding the player's own broods: with a positive
// edge and the model fitted for it, by what is in reach (a brood corridor
// that can reach a known structure strikes far more often); else by edge
// when the model has it; else the flat rate.
export function broodRate(model, edge, target = null) {
    const byTarget = model.attackFromBroodCorridorByTarget
    if (edge > 0 && target && byTarget) return byTarget[target][edge >= 2 ? '+2' : '+1']
    const byEdge = model.attackFromBroodCorridorByEdge
    if (!byEdge) return model.attackFromBroodCorridor
    return byEdge[edge <= 0 ? '0' : edge === 1 ? '+1' : '+2']
}

// P(attack) for a corridor that can: by the player's own broods there, else
// by edge and — with a positive edge — what is in reach.
export function attackRate(model, oppGrid, col, edge, ownBroods, odds) {
    if (edge <= 0 || !model.attackByTarget) return ownBroods ? broodRate(model, edge) : model.attackByEdge[edgeKey(edge)]
    const t = targetInReach(oppGrid, col, Math.min(5, edge), odds)
    if (ownBroods) return broodRate(model, edge, t)
    return model.attackByTarget[t][edge >= 2 ? '+2' : '+1']
}

export function playerPolicy(me, oppBoard, oppGrid, rng, model = MODEL) {
    let board = clone(me.board)
    const hf = me.homefront
    const kindOf = (c) => {
        const mine = (board[c].armyQty || 0) + (board[c].newArmies || 0) + (board[c].movedInArmies || 0)
        const opp = oppStrength(oppBoard[c])
        if (opp > 0 && opp >= mine) return 'threatened'
        if (basesOf(board[c]).length) return 'brood'
        return mine > 0 ? 'stack' : 'empty'
    }

    // Broods.
    for (let c = 0; c < board.length; c++) {
        if ((board[c].armyQty || 0) < rules().broodCost || rng() >= model.broodWhenAble) continue
        const pending = new Set((board[c].pendingBaseBuilds ?? []).flatMap(p => baseFootprint(p.centerS) ?? []))
        const spots = eligibleBaseCenters(hf, c).filter(s => !(baseFootprint(s) ?? []).some(x => pending.has(x)))
        if (!spots.length) continue
        const r = buildFort(board, board[c].id, spots[spots.length - 1])
        if (r.success) board = r.boardArr
    }
    // Moves.
    for (let c = 0; c < board.length; c++) {
        if (!(board[c].armyQty > 0) || rng() >= model.moveWhenAble) continue
        const r = moveUnit(board, board[c].id, rng() < 0.5 ? 'left' : 'right')
        if (r.success) board = r.boardArr
    }
    // Deploys, by kind of corridor.
    let owed = rules().armiesPerRound
    for (let guard = 0; owed > 0 && guard < 20; guard++) {
        const cols = board.map((_, c) => c).filter(c => room(board[c]) > 0 && !board[c].isAttacking)
        if (!cols.length) break
        const c = weightedPick(rng, cols, i => model.deployWeight[kindOf(i)] ?? 0.1)
        const r = deployArmy(board, board[c].id, owed)
        if (r.success) { board = r.boardArr; owed = r.armiesToDeploy }
    }
    // Attacks, by edge — aimed where the board says the structures are.
    const odds = stampOdds(oppGrid)
    const conBroods = new Set(oppGrid.broods.filter(b => !b.spent && b.underConstruction).flatMap(b => b.footprint))
    const targetPlots = Array(board.length).fill(null)
    const targetSubplots = Array(board.length).fill(null)
    for (let c = 0; c < board.length; c++) {
        if (!canAttack(board[c]).valid) continue
        const edge = calculateCombatStrength(board[c]) - oppStrength(oppBoard[c])
        if (rng() >= attackRate(model, oppGrid, c, edge, basesOf(board[c]).length > 0, odds)) continue
        const margin = Math.max(1, Math.min(5, edge))
        const cells = corridorCells(c)
        let best = cells[0], bestHit = -1
        for (const s of cells) {
            const hit = craterFootprint(s, margin).reduce((a, x) => a + odds[x] + (conBroods.has(x) ? 1 : 0), 0) + rng() * 0.01
            if (hit > bestHit) { best = s; bestHit = hit }
        }
        const aim = best
        board[c] = { ...board[c], isAttacking: true }
        targetSubplots[c] = aim
        targetPlots[c] = plotOf(aim).row
    }
    // Drop zones owed, like deploys.
    const dropZones = []
    const owedZones = Math.min(requiredDropZones(board), openSlots(board))
    for (let tries = 0; dropZones.length < owedZones && tries < 40; tries++) {
        const c = weightedPick(rng, board.map((_, i) => i), i => model.deployWeight[kindOf(i)] ?? 0.1)
        if (canPlaceDropZone(board, c, dropZones).valid) dropZones.push(c)
    }
    for (let c = 0; dropZones.length < owedZones && c < board.length; c++) {
        while (dropZones.length < owedZones && canPlaceDropZone(board, c, dropZones).valid) dropZones.push(c)
    }
    // The rare purchase.
    const rockets = []
    if ((me.yield ?? 0) >= rules().rocketCost && rng() < model.rocketWhenAffordable) {
        const cell = assetCells(hf).find(({ s, st }) => !(st.rocketCells || []).includes(s))
        if (cell) rockets.push(cell.s)
    }
    const orders = { board, targetPlots, targetSubplots, dropZones, rockets, drones: [] }
    return validateOrders(me, orders).ok ? orders : deployOnly(me)
}
